import CIcon from '@coreui/icons-react';
import DOMPurify from 'dompurify';
import { CButton } from '@coreui/react';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { debounce } from 'lodash';
import { useOutsideHandling } from '../../../../../../../utils';
import { CInput } from '../../../../../../migration/CInput';
import CTooltip from '../../../../../../migration/CTooltip';
import CLabel from '../../../../../../migration/CLabel';
import { FullListTableContext } from '.';
import { PERSONAL_MAIL } from '../../constants';

const Dropdown = ({ children, isOpen, target, onClose, fieldFull }) => (
     <div className="select-wrapper">
          {target}
          {fieldFull ? isOpen ? <Menu1>{children}</Menu1> : null : isOpen ? <Menu>{children}</Menu> : null}
          {isOpen ? <Blanket onClick={onClose} /> : null}
     </div>
);

const Menu1 = (props) => {
     return (
          <div
               style={{
                    borderRadius: 4,
                    marginTop: 8,
                    position: 'absolute',
                    zIndex: 2,
                    cursor: 'default',
               }}
               {...props}
          />
     );
};
const Menu = (props) => {
     return (
          <div
               style={{
                    borderRadius: 4,
                    marginTop: 8,
                    position: 'absolute',
                    zIndex: 2,
                    width: '100%',
                    cursor: 'default',
               }}
               {...props}
          />
     );
};

const Blanket = (props) => (
     <div
          style={{
               bottom: 0,
               left: 0,
               top: 0,
               right: 0,
               position: 'absolute',
               zIndex: 1,
          }}
          {...props}
     />
);
const TableSelectSearchV2 = (props) => {
     const {
          id,
          isBlur,
          placeholder,
          options,
          onToggle,
          onChange,
          onInputChange,
          onClickRemove,
          value,
          isDisabled,
          isShowRemove,
          className,
          classNameWrapper,
          selectInputType = true,
          displayPlaceholder,
          fieldFull,
          isMulti,
          error,
          dataVariable = true,
          setShowPopupCreateVariables,
          setStorageType = () => { },
          setVariableKeyId = () => { },
          valueFrom,
          currentDomain,
     } = props;

     const { isShowAddNew, handleAddNewData } = useContext(FullListTableContext);

     let newOptions = options;

     if (currentDomain) {
          newOptions = options.filter((item) => !currentDomain.includes(item.domain));
     }

     const wrapperRef = useRef(null);
     const filterInputRef = useRef(null);
     const valueInputRef = useRef(null);
     const [isOpen, setIsOpen] = useState(false);
     const [initValue, setInitValue] = useState(value);
     const inputValue = initValue?.label && !displayPlaceholder ? initValue.label : placeholder;
     const [variableFilter, setVariableFilter] = useState(''); // Current text in the insert data layer variable box filter
     const [openTypeVariable, setOpenTypeVariable] = useState({});
     const [showScroll, setShowScroll] = useState(false);
     let variableFilterLowerCase = variableFilter.toLowerCase();
     // Automatic variable options which are displayed in insert data layer variable box
     let variableOptionsFound = newOptions.filter((option) => option?.label?.toLowerCase().includes(variableFilterLowerCase));
     const [variableOptions, setVariableOptions] = useState([]);
     const itemsLoadMore = 20;
     const valueField = value?.label || '';

     useEffect(() => {
          if (wrapperRef.current) {
               if (wrapperRef.current.scrollHeight > wrapperRef.current.clientHeight) {
                    setShowScroll(true);
               } else {
                    setShowScroll(false);
               }
          }
     }, [openTypeVariable]);

     useEffect(() => {
          if (value) {
               setInitValue(value);
          } else {
               setInitValue(null);
          }
     }, [value]);

     const clickOutside = useCallback(() => {
          setIsOpen(false);
          setOpenTypeVariable({});
          setShowScroll(false);
          if (dataVariable) {
               setVariableOptions([]);
          }
          setVariableFilter('');
     }, [dataVariable]);

     useOutsideHandling(wrapperRef, clickOutside, 'mouseup');

     useEffect(() => {
          if (!isMulti) {
               if (isOpen && filterInputRef.current) {
                    setTimeout(() => {
                         filterInputRef.current.focus();
                    }, 0);
               }
               if (!isOpen && valueInputRef.current) valueInputRef.current.focus();
          }
     }, [isMulti, isOpen]);

     const toggleOpen = (e) => {
          const disableInput = e ? !e.target.classList.contains('disable-select') : true;
          if (disableInput)
               setTimeout(() => {
                    setIsOpen(!isOpen);
                    onToggle();
                    if (valueInputRef.current) valueInputRef.current.readOnly = false;
               }, 1);
          if (!dataVariable) {
               // eslint-disable-next-line
               setOpenTypeVariable((prevState) => ({ ...prevState, ['Data Layer Variable']: !prevState['Data Layer Variable'] }));
          }
     };

     const onSelectChange = (e) => {
          if (isMulti) {
               let newValue = [];
               if (value?.length > 0) {
                    newValue = [...value, e];
               } else {
                    newValue = [e];
               }
               const { onChange, name, hasChange } = props;
               onChange(name, newValue);
               if (hasChange) {
                    hasChange();
               }
          } else {
               valueInputRef.current.focus();
               toggleOpen();
               setInitValue(e);
               onChange(e);
          }
     };

     const handleOpenTypeVariable = (id) => {
          setOpenTypeVariable((prevState) => ({ ...prevState, [id]: !prevState[id] }));
          if (openTypeVariable['Data Layer Variable']) {
               setVariableOptions([]);
          }
          loadMoreItems();
     };

     useEffect(() => {
          if (variableFilter) {
               searchItems(newOptions, variableFilter);

               return () => {
                    searchItems.cancel();
               };
          } else {
               const newVariableOptions = newOptions.filter((item) => item?.label).slice(0, itemsLoadMore);
               setVariableOptions(newVariableOptions);
               if (wrapperRef.current) {
                    wrapperRef.current.scrollTo({ top: 0 });
               }
          }
     }, [variableFilter, options]); // eslint-disable-line

     const searchItems = useMemo(() => {
          return debounce((items, query) => {
               if (query) {
                    const filteredItems = items
                         .filter((item) => item?.label?.toLowerCase().includes(query.toLowerCase()))
                         .slice(0, itemsLoadMore);
                    setVariableOptions(filteredItems);
               }
          }, 200);
     }, []);

     const handleSearch = (e) => {
          onInputChange(e.trim());

          setVariableFilter(e.trim());
          const searchObject = {};

          const findDatalayerVariable = variableOptionsFound.find((i) => {
               return i?.label?.toLowerCase().includes(e.toLowerCase());
          });
          if (findDatalayerVariable) {
               searchObject['Data Layer Variable'] = true;
          }
          if (Object.keys(searchObject).length > 0) {
               setOpenTypeVariable(searchObject);
          }
     };

     const handleRemoveVariable = (i) => {
          const newValue = [...value];
          newValue.splice(i, 1);
          const { onChange, name, hasChange } = props;
          onChange(name, newValue);
          if (hasChange) {
               hasChange();
          }
          setTimeout(() => {
               setIsOpen(false);
          }, 2);
     };

     const handleCheckShowData = (val, option) => {
          const checkVal = val.find((item) => {
               if (item?.label && item.label === option.label) {
                    return true;
               }
               return false;
          });
          return checkVal;
     };

     const handleCheckLength = (val, variableOptions) => {
          let length = 0;
          variableOptions.forEach((el) => {
               const checkVal = val.find((item) => {
                    if (item?.label && item.label === el.label) {
                         return true;
                    }
                    return false;
               });
               if (!checkVal) {
                    length = length + 1;
               }
          });
          return length;
     };

     const handleClick = (option) => {
          if (option.isCustom) {
               setShowPopupCreateVariables(true);
               setStorageType(valueFrom);
               setVariableKeyId(id);
          } else {
               onSelectChange(option);
          }
     };

     const handleClickAddNew = () => {
          handleAddNewData()
          toggleOpen();
     };

     const loadMoreItems = () => {
          const currentLength = variableOptions.length;
          const moreItems = variableOptionsFound?.slice(currentLength, currentLength + itemsLoadMore);

          if (variableOptions.length + moreItems.length <= variableOptionsFound.length) {
               setVariableOptions((prevItems) => [...prevItems, ...moreItems]);
          }
     };

     const handleScroll = () => {
          if (wrapperRef.current?.scrollTop + wrapperRef.current.clientHeight >= wrapperRef.current.scrollHeight) {
               loadMoreItems();
          }
     };

     return (
          <div
               className={`select-insert-variable ${classNameWrapper} ${isBlur ? ' select-react-customize blur' : ' select-react-customize'}${!value || !value.value ? ' not-selected' : ''
                    }`}
               ref={wrapperRef}
          >
               <Dropdown
                    fieldFull={fieldFull}
                    className={className}
                    isOpen={isOpen}
                    onClose={toggleOpen}
                    target={
                         selectInputType ? (
                              <CInput
                                   onClick={() => toggleOpen()}
                                   readOnly
                                   value={inputValue}
                                   disabled={isDisabled}
                                   className={`${isDisabled ? 'disable-select' : ''}`}
                              />
                         ) : (
                              <>
                                   {isMulti ? (
                                        <>
                                             <div
                                                  className={`stretch-container select-multiple-variable-field form-control ${isDisabled ? 'disabled' : ''
                                                       } ${isDisabled ? 'disable-select' : ''} `}
                                                  onClick={toggleOpen}
                                             >
                                                  {value?.length > 0 ? (
                                                       <ul>
                                                            {value.map((v, i) => {
                                                                 return (
                                                                      <Fragment key={i}>
                                                                           {v?.value && (
                                                                                <li>
                                                                                     {v.value}{' '}
                                                                                     <CButton
                                                                                          onClick={() => handleRemoveVariable(i)}
                                                                                          className="icon-remove-multi"
                                                                                     >
                                                                                          <CIcon
                                                                                               icon="iconRemoveMultiSelect"
                                                                                               width={10}
                                                                                               height={10}
                                                                                          />
                                                                                     </CButton>{' '}
                                                                                </li>
                                                                           )}
                                                                      </Fragment>
                                                                 );
                                                            })}
                                                       </ul>
                                                  ) : (
                                                       <div className="place-holder-select">{placeholder}</div>
                                                  )}
                                             </div>
                                        </>
                                   ) : (
                                        <div className="select-search-input-wrapper">
                                             <div

                                                  className={`stretch-container form-control ${isDisabled ? 'disable-select' : ''} `}
                                                  onClick={toggleOpen}
                                                  ref={valueInputRef}
                                             >
                                                  {inputValue}
                                             </div>

                                             {isShowRemove && (
                                                  <span className="btn-remove-row">
                                                       <CTooltip content="Remove row" placement="bottom">
                                                            <i className="fal fa-times text-danger" onClick={() => onClickRemove()}></i>
                                                       </CTooltip>
                                                  </span>
                                             )}
                                        </div>
                                   )}
                              </>
                         )
                    }
               >
                    <div className={`insert-variable-dropdown`}>
                         <ul
                              ref={wrapperRef}
                              className={`${newOptions.length === 0 ? 'value-no-options-available no-options' : ''} ${showScroll ? 'have-scroll' : ''}`}
                              onScroll={handleScroll}
                         >
                              <li className="variable-filter">
                                   <CInput
                                        innerRef={filterInputRef}
                                        type="text"
                                        value={variableFilter}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        placeholder="Select or type to search"
                                   />
                              </li>
                              {dataVariable && (
                                   <li
                                        className="d-flex align-items-center justify-content-between border-bottom border-top type-variable"
                                        onClick={() => handleOpenTypeVariable('Data Layer Variable')}
                                   >
                                        <strong>Data Layer Variable</strong>
                                        {openTypeVariable['Data Layer Variable'] ? (
                                             <strong>
                                                  {isMulti ? (
                                                       <>{handleCheckLength(value, variableOptionsFound)}</>
                                                  ) : (
                                                       <>{variableOptionsFound.length}</>
                                                  )}
                                             </strong>
                                        ) : (
                                             <CIcon icon="iconArrowDownStrong" />
                                        )}
                                   </li>
                              )}
                              {(openTypeVariable['Data Layer Variable'] || !dataVariable) && (
                                   <>
                                        {variableOptions.length > 0 ? (
                                             <>
                                                  {variableOptions.map((option, index) => {
                                                       const activeValue = valueField === option.label;
                                                       const re = new RegExp(variableFilter, 'gi');
                                                       let newVariable = option.label;
                                                       if (variableFilter) {
                                                            newVariable = newVariable.replace(re, `<strong>${variableFilter}</strong>`);
                                                       }
                                                       let checkShowValue = false;
                                                       if (isMulti) {
                                                            checkShowValue = handleCheckShowData(value, option);
                                                       }
                                                       const sanitizedNewVariable = DOMPurify.sanitize(`${newVariable}`);
                                                       return (
                                                            <React.Fragment key={index}>
                                                                 {!checkShowValue ? (
                                                                      <li
                                                                           className={`border-bottom d-flex align-items-center ${activeValue ? 'active-variable' : ''
                                                                                } ${option.isCustom ? 'custom' : ''}`}
                                                                           key={option.label}
                                                                           onClick={() => handleClick(option)}
                                                                      >
                                                                           <span dangerouslySetInnerHTML={{ __html: sanitizedNewVariable }}></span>
                                                                      </li>
                                                                 ) : (
                                                                      ''
                                                                 )}
                                                            </React.Fragment>
                                                       );
                                                  })}

                                                  {variableOptions.length < 5 && isShowAddNew && !variableOptions.find((item) => item.domain === variableFilter) && (
                                                       <li className="option-add-new mb-2 d-flex align-items-center justify-content-center" style={{ cursor: "pointer" }}>
                                                            <CButton className="btn-add-new d-flex align-items-center justify-content-center p-0 mt-2" onClick={() => handleClickAddNew()} style={{}}>
                                                                 <CIcon icon="iconAddField" className="icon-add" />
                                                                 <CLabel className="label-add mb-0 ml-2" style={{ cursor: "pointer" }}>Add New</CLabel>
                                                            </CButton>
                                                       </li>
                                                  )}
                                             </>
                                        ) : (
                                             <li className="no-options border-bottom d-flex align-items-center">
                                                  {isShowAddNew ? (
                                                       <div className="option-add-new mb-2">
                                                            <CButton className="btn-add-new" onClick={() => handleClickAddNew()}>
                                                                 <CIcon icon="iconAddField" className="icon-add" />
                                                                 <CLabel className="label-add mb-0">Add New</CLabel>
                                                            </CButton>

                                                            <p className="add-new-description">
                                                                 No matches from your Target Account list. Click to add them.
                                                            </p>
                                                       </div>
                                                  ) : (
                                                       PERSONAL_MAIL.includes(variableFilter) ?
                                                            <p className="text">This person email is not supported to be a target account.</p>
                                                            : 
                                                            <p className="text">No domains found</p>
                                                  )}
                                             </li>
                                        )}
                                   </>
                              )}
                         </ul>
                    </div>
               </Dropdown>
               {error && typeof error === 'string' ? <p style={{ color: '#e55353', fontSize: '80%' }}>{error}</p> : null}
          </div>
     );
};
TableSelectSearchV2.propTypes = {
     id: PropTypes.string,
     isBlur: PropTypes.bool,
     placeholder: PropTypes.string,
     options: PropTypes.array,
     onChange: PropTypes.func,
     value: PropTypes.object,
     isDisabled: PropTypes.bool,
     className: PropTypes.string,
     classNamePrefix: PropTypes.string,
     classNameWrapper: PropTypes.string,
     selectInputType: PropTypes.bool,
     displayPlaceholder: PropTypes.bool,
     filterOption: PropTypes.func,
     selectStyles: PropTypes.object,
     isMulti: PropTypes.bool,
     onToggle: PropTypes.func,
     onInputChange: PropTypes.func,
     onClickRemove: PropTypes.func,
     isShowRemove: PropTypes.bool,
     fieldFull: PropTypes.any,
     error: PropTypes.object,
     dataVariable: PropTypes.any,
     setShowPopupCreateVariables: PropTypes.func,
     setStorageType: PropTypes.func,
     setVariableKeyId: PropTypes.func,
     valueFrom: PropTypes.any,
     currentDomain: PropTypes.string,
     name: PropTypes.string,
     hasChange: PropTypes.bool,
};

Dropdown.propTypes = {
     isOpen: PropTypes.bool,
     onClose: PropTypes.func,
     target: PropTypes.string,
     children: PropTypes.node,
     fieldFull: PropTypes.any,
};

export default TableSelectSearchV2;
